import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { useApexTheme, mergeThemeOptions } from '../utils/apexTheme';
import { aggregateMonthlyReturns } from '../utils/backtestCalculations';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const MonthlyHeatmap = ({ returnsSeries, height = 250 }) => {
  const { baseThemeOptions, colors, isDark } = useApexTheme();

  const { options, series } = useMemo(() => {
    if (!returnsSeries || returnsSeries.length === 0) return { options: null, series: [] };

    const monthlyReturns = aggregateMonthlyReturns(returnsSeries);

    // Get unique years
    const years = [...new Set(monthlyReturns.map(r => r.year))].sort();

    // Create series data for heatmap
    const seriesData = years.map(year => {
      const yearData = MONTHS.map((month, monthIndex) => {
        const dataPoint = monthlyReturns.find(r => r.year === year && r.month === monthIndex);
        return dataPoint ? dataPoint.return : null;
      });
      return {
        name: String(year),
        data: yearData.map((value, idx) => ({
          x: MONTHS[idx],
          y: value !== null ? parseFloat(value.toFixed(2)) : null
        }))
      };
    });

    // Find min/max for color scale
    const allValues = monthlyReturns.map(r => r.return);
    const maxAbs = Math.max(Math.abs(Math.min(...allValues)), Math.abs(Math.max(...allValues)));

    const componentOptions = {
      chart: {
        type: 'heatmap',
        height,
        toolbar: {
          show: false
        }
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => {
          if (val === null) return '';
          const sign = val >= 0 ? '+' : '';
          return sign + val.toFixed(1) + '%';
        },
        style: {
          fontSize: '10px',
          colors: [isDark ? '#fff' : '#000']
        }
      },
      plotOptions: {
        heatmap: {
          shadeIntensity: 0.5,
          radius: 4,
          useFillColorAsStroke: false,
          colorScale: {
            ranges: [
              { from: -maxAbs, to: -maxAbs * 0.5, color: '#d32f2f', name: 'Very Negative' },
              { from: -maxAbs * 0.5, to: -0.01, color: '#ef5350', name: 'Negative' },
              { from: -0.01, to: 0.01, color: isDark ? '#555' : '#9e9e9e', name: 'Neutral' },
              { from: 0.01, to: maxAbs * 0.5, color: '#66bb6a', name: 'Positive' },
              { from: maxAbs * 0.5, to: maxAbs, color: '#2e7d32', name: 'Very Positive' }
            ]
          }
        }
      },
      xaxis: {
        categories: MONTHS,
        labels: {
          style: {
            fontSize: '10px'
          }
        }
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '10px'
          }
        }
      },
      tooltip: {
        y: {
          formatter: (val) => {
            if (val === null) return 'No data';
            const sign = val >= 0 ? '+' : '';
            return sign + val.toFixed(2) + '%';
          }
        }
      },
      legend: {
        show: false
      }
    };

    return {
      options: mergeThemeOptions(baseThemeOptions, componentOptions),
      series: seriesData
    };
  }, [returnsSeries, height, baseThemeOptions, colors, isDark]);

  if (!options) {
    return (
      <div className="flex items-center justify-center text-base-content/60" style={{ height }}>
        No returns data available
      </div>
    );
  }

  return (
    <div>
      <h4 className="text-sm font-bold mb-2">Monthly Returns Heatmap</h4>
      <Chart
        options={options}
        series={series}
        type="heatmap"
        height={height}
      />
    </div>
  );
};

export default MonthlyHeatmap;
